// Modern Compliance Dashboard - Redesigned
// All content in single scrollable view (no tabs)
// Matches dashboard-new design with KPI cards

import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StaticMetricCardAlt } from '@/components/StaticMetricCardAlt';
import { Separator } from '@/components/ui/separator';
import { InsightsPanel } from '@/components/compliance/insights-panel';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  Shield,
  AlertTriangle,
  Target,
  MapPin,
  Package,
  Users,
  BarChart3,
  BookOpen,
  Building2,
  ArrowRight,
  Scale,
  Sparkles,
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { ComplianceSummary, AssetComplianceListItem } from '@/types/compliance';
import { mockComplianceSummary, mockAssetComplianceList } from '@/lib/mock-compliance-data';
import { getRegimeInfo, getStatusConfig } from '@/lib/compliance-data';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';

const getComplianceColor = (score: number): 'green' | 'yellow' | 'red' => {
  if (score >= 90) return 'green';
  if (score >= 75) return 'yellow';
  return 'red';
};

const getComplianceBadgeVariant = (rate: number): 'default' | 'secondary' | 'destructive' => {
  if (rate >= 90) return 'default';
  if (rate >= 75) return 'secondary';
  return 'destructive';
};

type ViewType = 'regulation' | 'location' | 'category';

const getViewTypeLabel = (viewType: ViewType): string => {
  if (viewType === 'regulation') return 'Regulation Type';
  if (viewType === 'location') return 'Location';
  return 'Category';
};

const getViewTypeArticle = (viewType: ViewType): string => {
  if (viewType === 'regulation') return 'a regulation';
  if (viewType === 'location') return 'a location';
  return 'a category';
};

export default function CompliancePage(): React.ReactElement {
  const navigate = useNavigate();
  const { modules } = useFeatureSettings();
  const complianceModule = modules.compliance;
  const isDemoMode = complianceModule?.mode === 'demo';
  const backendUrl = useGetBackendUrl();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [assetList, setAssetList] = useState<AssetComplianceListItem[]>([]);
  const [viewType, setViewType] = useState<ViewType>('regulation');
  const [selectedRegime, setSelectedRegime] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [navigationDialog, setNavigationDialog] = useState<{ open: boolean; assetId: string | null }>({ open: false, assetId: null });
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isInsightsPanelCollapsed, setIsInsightsPanelCollapsed] = useState(false);

  // Load data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        
        if (isDemoMode) {
          // Demo mode: use mock data
          await new Promise((resolve) => setTimeout(resolve, 300));
          setSummary(mockComplianceSummary);
          setAssetList(mockAssetComplianceList);
        } else {
          // Live mode: fetch from API
          try {
            const [summaryResponse, assetsResponse] = await Promise.all([
              fetch(`${backendUrl}/api/Compliance/dashboard`),
              fetch(`${backendUrl}/api/Compliance/asset-compliances`)
            ]);

            if (summaryResponse.ok && assetsResponse.ok) {
              const summaryData = await summaryResponse.json();
              const assetsData = await assetsResponse.json();
              console.log('Fetched compliance data from API:', { summaryData, assetsData });
              
              // Transform backend response to match frontend expectations
              const transformedSummary = {
                compliancePercentage: summaryData.overallCompliancePercentage || 0,
                totalAssets: summaryData.totalAssetsMonitored || 0,
                compliantAssets: Math.round((summaryData.totalAssetsMonitored || 0) * (summaryData.overallCompliancePercentage || 0) / 100),
                nonCompliantAssets: summaryData.overdueItems || 0,
                dueSoonAssets: summaryData.dueThisWeek || 0,
                unknownStatusAssets: summaryData.unknownStatusAssets || 0,
                trend90Days: 0, // Backend doesn't provide this yet
                kpis: summaryData.byRegulation?.map((reg: any) => ({
                  regimeCode: reg.regulation,
                  regimeName: reg.regulation,
                  totalAssets: reg.totalChecks,
                  compliant: reg.compliantChecks,
                  dueSoon: 0,
                  overdue: reg.totalChecks - reg.compliantChecks,
                  complianceRate: reg.compliancePercentage,
                  target: 95
                })) || [],
                byLocation: summaryData.byLocation?.map((loc: any) => ({
                  locationId: loc.location,
                  locationName: loc.location,
                  totalAssets: loc.assetCount,
                  compliant: Math.round(loc.assetCount * loc.compliancePercentage / 100),
                  dueSoon: 0,
                  overdue: loc.assetCount - Math.round(loc.assetCount * loc.compliancePercentage / 100),
                  complianceRate: loc.compliancePercentage
                })) || [],
                byCategory: summaryData.byCategory?.map((cat: any) => ({
                  category: cat.category,
                  totalAssets: cat.assetCount,
                  compliant: Math.round(cat.assetCount * cat.compliancePercentage / 100),
                  dueSoon: 0,
                  overdue: cat.assetCount - Math.round(cat.assetCount * cat.compliancePercentage / 100),
                  complianceRate: cat.compliancePercentage
                })) || [],
                upcomingDeadlines: summaryData.upcomingDeadlines || [],
                nonCompliantItems: summaryData.nonCompliantItems || []
              };
              
              setSummary(transformedSummary);
              setAssetList(assetsData);
            } else {
              console.error('API returned error status:', {
                summary: summaryResponse.status,
                assets: assetsResponse.status
              });
              setSummary(null);
              setAssetList([]);
            }
          } catch (apiError) {
            console.error('Failed to load compliance data from API:', apiError);
            // In live mode, don't fall back to demo data
            setSummary(null);
            setAssetList([]);
          }
        }
      } catch (error) {
        console.error('Failed to load compliance data:', error);
        if (!isDemoMode) {
          // In live mode, show empty state on error
          setSummary(null);
          setAssetList([]);
        }
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [isDemoMode, backendUrl]);

  // Set first item as selected by default based on view type
  useEffect(() => {
    if (summary) {
      if (viewType === 'regulation' && summary.kpis && summary.kpis.length > 0 && !selectedRegime) {
        setSelectedRegime(summary.kpis[0].regimeCode);
      } else if (viewType === 'location' && summary.byLocation && summary.byLocation.length > 0 && !selectedLocation) {
        setSelectedLocation(summary.byLocation[0].locationId);
      } else if (viewType === 'category' && summary.byCategory && summary.byCategory.length > 0 && !selectedCategory) {
        setSelectedCategory(summary.byCategory[0].category);
      }
    }
  }, [summary, viewType, selectedRegime, selectedLocation, selectedCategory]);

  // Get selected regulation data
  const selectedRegimeData = useMemo(() => {
    if (!selectedRegime || !summary || !summary.kpis) return null;
    return summary.kpis.find(kpi => kpi.regimeCode === selectedRegime);
  }, [selectedRegime, summary]);

  // Get selected location data
  const selectedLocationData = useMemo(() => {
    if (!selectedLocation || !summary || !summary.byLocation) return null;
    return summary.byLocation.find(loc => loc.locationId === selectedLocation);
  }, [selectedLocation, summary]);

  // Get selected category data
  const selectedCategoryData = useMemo(() => {
    if (!selectedCategory || !summary || !summary.byCategory) return null;
    return summary.byCategory.find(cat => cat.category === selectedCategory);
  }, [selectedCategory, summary]);

  // AI Insights
  const insights = useMemo(() => {
    if (!summary) return [];
    
    const result: Array<{
      type: string;
      icon: React.ComponentType<{ className?: string }>;
      title: string;
      description: string;
      action: string;
      onClick: () => void;
    }> = [];
    
    if (summary.nonCompliantAssets > 0) {
      result.push({
        type: 'critical',
        icon: AlertTriangle,
        title: 'Immediate Action Required',
        description: `${summary.nonCompliantAssets} assets are overdue for compliance checks. Prioritise high-risk items.`,
        action: 'View Overdue',
        onClick: () => navigate('/compliance/assets?status=overdue')
      });
    }
    
    if (summary.dueSoonAssets > 5) {
      result.push({
        type: 'warning',
        icon: Clock,
        title: 'Plan Ahead',
        description: `${summary.dueSoonAssets} compliance checks due in the next 30 days. Schedule inspections now.`,
        action: 'View Schedule',
        onClick: () => navigate('/compliance/assets?status=due-soon')
      });
    }
    
    if (summary.trend90Days > 0) {
      result.push({
        type: 'success',
        icon: TrendingUp,
        title: 'Improving Compliance',
        description: `Compliance rate improved by ${summary.trend90Days}% over the last 90 days. Keep up the good work!`,
        action: 'View Trends',
        onClick: () => {}
      });
    }
    
    // Guard against missing kpis array
    if (summary.kpis && Array.isArray(summary.kpis)) {
      const lowPerformingKPI = summary.kpis.find(kpi => kpi.complianceRate < kpi.target);
      if (lowPerformingKPI) {
        result.push({
          type: 'info',
          icon: Target,
          title: `${lowPerformingKPI.regimeCode} Below Target`,
          description: `${lowPerformingKPI.regimeName} compliance at ${lowPerformingKPI.complianceRate.toFixed(1)}% (target: ${lowPerformingKPI.target}%). Focus resources here.`,
          action: 'View Details',
          onClick: () => navigate(`/compliance/assets?regime=${lowPerformingKPI.regimeCode}`)
        });
      }
    }
    
    return result;
  }, [summary, navigate]);

  const handleExport = () => {
    console.log('Export compliance report');
  };

  const handleAssetClick = (assetId: string) => {
    setNavigationDialog({ open: true, assetId });
  };

  const confirmNavigation = () => {
    if (navigationDialog.assetId) {
      navigate(`/assets?id=${navigationDialog.assetId}`);
    }
    setNavigationDialog({ open: false, assetId: null });
  };

  const renderLoadingSkeleton = (): React.ReactElement => (
    <>
      {/* KPI Cards Skeleton */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-5">
        <Skeleton key="skeleton-kpi-compliant" className="h-[140px]" />
        <Skeleton key="skeleton-kpi-overdue" className="h-[140px]" />
        <Skeleton key="skeleton-kpi-due-soon" className="h-[140px]" />
        <Skeleton key="skeleton-kpi-overall" className="h-[140px]" />
        <Skeleton key="skeleton-kpi-trend" className="h-[140px]" />
      </div>
      {/* Content Skeleton */}
      <div className="space-y-4">
        <Skeleton className="h-[400px] w-full" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    </>
  );

  const renderErrorState = (): React.ReactElement => (
    <div className="flex h-full items-center justify-center">
      <Card className="w-96">
        <CardContent className="pt-6">
          <div className="text-center text-red-500">
            <AlertCircle className="mx-auto h-12 w-12 mb-4" />
            <p>Failed to load compliance data</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      {/* Page Header */}
      <div className="bg-card border-b">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                Compliance Management
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Physical asset compliance management for UK HSE & British Standards
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleExport}>
                <Download className="mr-2 h-4 w-4" />
                Export Report
              </Button>
            </div>
          </div>
        </div>
      </div>



      {/* Main Content Area with Insights Panel */}
      <div className="flex-1 flex overflow-hidden">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto">
        <div className="p-6 space-y-6">
          {loading && renderLoadingSkeleton()}
          {!loading && !summary && renderErrorState()}
          {!loading && summary && (
            <>
          {/* KPI Cards */}
          <div className="grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-5">
            <StaticMetricCardAlt
              title="Compliant Assets"
              value={`${summary.compliantAssets}`}
              change={`+${summary.trend90Days}%`}
              changeLabel="from last 90 days"
              icon={CheckCircle}
              variant="info"
              onClick={() => navigate('/compliance/assets?status=compliant')}
            />

            <StaticMetricCardAlt
              title="Overdue Items"
              value={`${summary.nonCompliantAssets}`}
              change="Action needed"
              changeLabel={`${((summary.nonCompliantAssets / summary.totalAssets) * 100).toFixed(1)}% of total`}
              icon={AlertCircle}
              variant="info"
              onClick={() => navigate('/compliance/assets?status=overdue')}
            />

            <StaticMetricCardAlt
              title="Due Soon (30 days)"
              value={`${summary.dueSoonAssets}`}
              change="Schedule now"
              changeLabel={`${((summary.dueSoonAssets / summary.totalAssets) * 100).toFixed(1)}% of total`}
              icon={Clock}
              variant="info"
              onClick={() => navigate('/compliance/assets?status=due-soon')}
            />

            <StaticMetricCardAlt
              title="Overall Compliance"
              value={`${summary.compliancePercentage.toFixed(1)}%`}
              change={summary.compliancePercentage >= 95 ? "On target" : `${(95 - summary.compliancePercentage).toFixed(1)}% to go`}
              changeLabel="Target: 95%"
              icon={Target}
              variant="info"
            />

            <StaticMetricCardAlt
              title="Compliance Trend"
              value={`${summary.trend90Days > 0 ? '+' : ''}${summary.trend90Days}%`}
              change={summary.trend90Days > 0 ? "Improving" : "Declining"}
              changeLabel="90-day performance"
              icon={summary.trend90Days > 0 ? TrendingUp : TrendingDown}
              variant="info"
            />
          </div>

          {/* Compliance by Type - Unified View with Tabs */}
          <Card className="lg:col-span-6 flex flex-col min-h-[800px]">
            <CardHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {viewType === 'regulation' && <Shield className="h-5 w-5" />}
                    {viewType === 'location' && <MapPin className="h-5 w-5" />}
                    {viewType === 'category' && <Package className="h-5 w-5" />}
                    Compliance by {getViewTypeLabel(viewType)}
                  </CardTitle>
                  <CardDescription>
                    Select {getViewTypeArticle(viewType)} to view detailed information and compliance status
                  </CardDescription>
                </div>
                <Tabs value={viewType} onValueChange={(value) => setViewType(value as ViewType)}>
                  <TabsList>
                    <TabsTrigger value="regulation">
                      <Shield className="h-4 w-4 mr-2" />
                      Regulation
                    </TabsTrigger>
                    <TabsTrigger value="location">
                      <MapPin className="h-4 w-4 mr-2" />
                      Location
                    </TabsTrigger>
                    <TabsTrigger value="category">
                      <Package className="h-4 w-4 mr-2" />
                      Category
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col min-h-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: List Panel - Scrollable */}
                <div className="lg:max-h-[800px]">
                  <ScrollArea className="h-full w-full">
                    <ScrollAreaViewport className="h-full">
                      <div className="pr-4">
                      {/* Regulation Type View */}
                      {viewType === 'regulation' && summary.kpis && summary.kpis.map((kpi, index) => {
                        const isSelected = selectedRegime === kpi.regimeCode;
                        const complianceColor = getComplianceColor(kpi.complianceRate);
                        
                        // Calculate counts for the stacked bar
                        const compliantCount = kpi.compliant || 0;
                        const dueSoonCount = kpi.dueSoon || 0;
                        const overdueCount = kpi.overdue || 0;
                        const totalCount = kpi.totalAssets;
                        
                        const compliantPercent = (compliantCount / totalCount) * 100;
                        const dueSoonPercent = (dueSoonCount / totalCount) * 100;
                        const overduePercent = (overdueCount / totalCount) * 100;
                        
                        return (
                          <React.Fragment key={kpi.regimeCode}>
                          <button 
                            type="button"
                            className={cn(
                              "w-full text-left cursor-pointer p-3 rounded-lg transition-all border-2",
                              isSelected 
                                ? "bg-accent border-primary" 
                                : "hover:bg-accent/50 border-transparent"
                            )}
                            onClick={() => setSelectedRegime(kpi.regimeCode)}
                          >
                            <div className="space-y-2">
                              {/* Title and Percentage */}
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-sm truncate">{kpi.regimeCode}</h4>
                                  <p className="text-xs text-muted-foreground truncate">{kpi.regimeName}</p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <span className={cn(
                                    "text-lg font-bold",
                                    complianceColor === 'green' && "text-green-600",
                                    complianceColor === 'yellow' && "text-yellow-600",
                                    complianceColor === 'red' && "text-red-600"
                                  )}>
                                    {kpi.complianceRate.toFixed(0)}%
                                  </span>
                                </div>
                              </div>
                              
                              {/* Stacked Progress Bar */}
                              <div className="relative h-6 bg-muted rounded-md overflow-hidden">
                                <div className="absolute inset-0 flex">
                                  {compliantPercent > 0 && (
                                    <div 
                                      className="bg-green-200 dark:bg-green-800/40 flex items-center justify-center text-green-800 dark:text-green-200 text-xs font-medium"
                                      style={{ width: `${compliantPercent}%` }}
                                    >
                                      {compliantCount}
                                    </div>
                                  )}
                                  {dueSoonPercent > 0 && (
                                    <div 
                                      className="bg-yellow-200 dark:bg-yellow-800/40 flex items-center justify-center text-yellow-800 dark:text-yellow-200 text-xs font-medium"
                                      style={{ width: `${dueSoonPercent}%` }}
                                    >
                                      {dueSoonCount}
                                    </div>
                                  )}
                                  {overduePercent > 0 && (
                                    <div 
                                      className="bg-red-200 dark:bg-red-800/40 flex items-center justify-center text-red-800 dark:text-red-200 text-xs font-medium"
                                      style={{ width: `${overduePercent}%` }}
                                    >
                                      {overdueCount}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Stats Row */}
                              <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-3">
                                  <span className="text-muted-foreground">
                                    Total Assets: <span className="font-medium text-foreground">{totalCount}</span>
                                  </span>
                                  <span className="flex items-center gap-1 text-green-600">
                                    <CheckCircle className="h-3 w-3" />
                                    <span className="font-medium">{compliantCount}</span> Compliant
                                  </span>
                                  <span className="flex items-center gap-1 text-yellow-600">
                                    <Clock className="h-3 w-3" />
                                    <span className="font-medium">{dueSoonCount}</span> Due Soon
                                  </span>
                                  <span className="flex items-center gap-1 text-red-600">
                                    <AlertCircle className="h-3 w-3" />
                                    <span className="font-medium">{overdueCount}</span> Overdue
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>
                          {index < summary.kpis.length - 1 && (
                            <Separator className="my-2" />
                          )}
                        </React.Fragment>
                        );
                      })}

                      {/* Location View */}
                      {viewType === 'location' && summary.byLocation.map((location, index) => {
                        const isSelected = selectedLocation === location.locationId;
                        const complianceColor = getComplianceColor(location.complianceScore);
                        
                        const compliantCount = location.compliant || 0;
                        const totalCount = location.totalAssets;
                        const nonCompliantCount = totalCount - compliantCount;
                        
                        const compliantPercent = (compliantCount / totalCount) * 100;
                        const nonCompliantPercent = (nonCompliantCount / totalCount) * 100;
                        
                        return (
                          <React.Fragment key={location.locationId}>
                          <button 
                            type="button"
                            className={cn(
                              "w-full text-left cursor-pointer p-3 rounded-lg transition-all border-2",
                              isSelected 
                                ? "bg-accent border-primary" 
                                : "hover:bg-accent/50 border-transparent"
                            )}
                            onClick={() => setSelectedLocation(location.locationId)}
                          >
                            <div className="space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-sm truncate">{location.locationName}</h4>
                                  <p className="text-xs text-muted-foreground">Location</p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <span className={cn(
                                    "text-lg font-bold",
                                    complianceColor === 'green' && "text-green-600",
                                    complianceColor === 'yellow' && "text-yellow-600",
                                    complianceColor === 'red' && "text-red-600"
                                  )}>
                                    {location.complianceScore.toFixed(0)}%
                                  </span>
                                </div>
                              </div>
                              
                              <div className="relative h-6 bg-muted rounded-md overflow-hidden">
                                <div className="absolute inset-0 flex">
                                  {compliantPercent > 0 && (
                                    <div 
                                      className="bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-900 dark:text-green-100 text-xs font-medium"
                                      style={{ width: `${compliantPercent}%` }}
                                    >
                                      {compliantPercent > 10 && compliantCount}
                                    </div>
                                  )}
                                  {nonCompliantPercent > 0 && (
                                    <div 
                                      className="bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-900 dark:text-red-100 text-xs font-medium"
                                      style={{ width: `${nonCompliantPercent}%` }}
                                    >
                                      {nonCompliantPercent > 10 && nonCompliantCount}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-3">
                                  <span className="text-muted-foreground">
                                    Total: <span className="font-medium text-foreground">{totalCount}</span>
                                  </span>
                                  <span className="flex items-center gap-1 text-green-600">
                                    <CheckCircle className="h-3 w-3" />
                                    {compliantCount}
                                  </span>
                                  <span className="flex items-center gap-1 text-red-600">
                                    <AlertCircle className="h-3 w-3" />
                                    {nonCompliantCount}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>
                          {index < summary.byLocation.length - 1 && (
                            <Separator className="my-2" />
                          )}
                        </React.Fragment>
                        );
                      })}

                      {/* Category View */}
                      {viewType === 'category' && summary.byCategory.map((category, index) => {
                        const isSelected = selectedCategory === category.category;
                        const complianceColor = getComplianceColor(category.complianceScore);
                        
                        const compliantCount = category.compliant || 0;
                        const totalCount = category.totalAssets;
                        const nonCompliantCount = totalCount - compliantCount;
                        
                        const compliantPercent = (compliantCount / totalCount) * 100;
                        const nonCompliantPercent = (nonCompliantCount / totalCount) * 100;
                        
                        return (
                          <React.Fragment key={category.category}>
                          <button 
                            type="button"
                            className={cn(
                              "w-full text-left cursor-pointer p-3 rounded-lg transition-all border-2",
                              isSelected 
                                ? "bg-accent border-primary" 
                                : "hover:bg-accent/50 border-transparent"
                            )}
                            onClick={() => setSelectedCategory(category.category)}
                          >
                            <div className="space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-sm truncate">{category.category}</h4>
                                  <p className="text-xs text-muted-foreground">Asset Category</p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <span className={cn(
                                    "text-lg font-bold",
                                    complianceColor === 'green' && "text-green-600",
                                    complianceColor === 'yellow' && "text-yellow-600",
                                    complianceColor === 'red' && "text-red-600"
                                  )}>
                                    {category.complianceScore.toFixed(0)}%
                                  </span>
                                </div>
                              </div>
                              
                              <div className="relative h-6 bg-muted rounded-md overflow-hidden">
                                <div className="absolute inset-0 flex">
                                  {compliantPercent > 0 && (
                                    <div 
                                      className="bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-900 dark:text-green-100 text-xs font-medium"
                                      style={{ width: `${compliantPercent}%` }}
                                    >
                                      {compliantPercent > 10 && compliantCount}
                                    </div>
                                  )}
                                  {nonCompliantPercent > 0 && (
                                    <div 
                                      className="bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-900 dark:text-red-100 text-xs font-medium"
                                      style={{ width: `${nonCompliantPercent}%` }}
                                    >
                                      {nonCompliantPercent > 10 && nonCompliantCount}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-3">
                                  <span className="text-muted-foreground">
                                    Total: <span className="font-medium text-foreground">{totalCount}</span>
                                  </span>
                                  <span className="flex items-center gap-1 text-green-600">
                                    <CheckCircle className="h-3 w-3" />
                                    {compliantCount}
                                  </span>
                                  <span className="flex items-center gap-1 text-red-600">
                                    <AlertCircle className="h-3 w-3" />
                                    {nonCompliantCount}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>
                          {index < summary.byCategory.length - 1 && (
                            <Separator className="my-2" />
                          )}
                        </React.Fragment>
                        );
                      })}
                      </div>
                    </ScrollAreaViewport>
                    <ScrollAreaScrollbar orientation="vertical" />
                  </ScrollArea>
                </div>

                {/* Right: Details Panel - Expands based on content */}
                <div>
                  {/* Regulation View */}
                  {viewType === 'regulation' && selectedRegimeData && selectedRegime && (
                    <div className="space-y-6">
                        {/* Regulation Header */}
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-2xl font-bold">{selectedRegimeData.regimeCode}</h3>
                              <p className="text-sm text-muted-foreground mt-1">{selectedRegimeData.regimeName}</p>
                            </div>
                            <Badge 
                              variant={getComplianceBadgeVariant(selectedRegimeData.complianceRate)}
                              className="text-lg px-3 py-1"
                            >
                              {selectedRegimeData.complianceRate.toFixed(0)}%
                            </Badge>
                          </div>
                        </div>

                        <Separator />

                        {/* Regulation Information */}
                        {(() => {
                          const regimeInfo = getRegimeInfo(selectedRegime);
                          return regimeInfo ? (
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <h4 className="font-semibold flex items-center gap-2">
                                  <BookOpen className="h-4 w-4" />
                                  What is this regulation?
                                </h4>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                  {regimeInfo.description}
                                </p>
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Building2 className="h-3 w-3" />
                                    Regulatory Body
                                  </p>
                                  <p className="text-sm font-medium">{regimeInfo.regulatoryBody}</p>
                                </div>
                                <div className="space-y-1">
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Scale className="h-3 w-3" />
                                    Legislation
                                  </p>
                                  <p className="text-sm font-medium">{regimeInfo.legislationReference}</p>
                                </div>
                              </div>

                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm flex items-center gap-2">
                                  Inspection Intervals
                                  <span className="text-xs font-normal text-muted-foreground">(Suggested)</span>
                                </h4>
                                <p className="text-xs text-muted-foreground italic mb-2">
                                  These are typical intervals. Your business may have different requirements based on risk assessments and operational needs.
                                </p>
                                <ul className="space-y-1">
                                  {regimeInfo.defaultIntervals.map((interval) => (
                                    <li key={interval} className="text-sm text-muted-foreground flex items-start gap-2">
                                      <span className="text-primary mt-1">•</span>
                                      <span>{interval}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm">Key Points</h4>
                                <ul className="space-y-1">
                                  {regimeInfo.keyPoints.map((point) => (
                                    <li key={point} className="text-sm text-muted-foreground flex items-start gap-2">
                                      <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                      <span>{point}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              {regimeInfo.guidanceUrl && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full"
                                  onClick={() => window.open(regimeInfo.guidanceUrl, '_blank')}
                                >
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View Official Guidance
                                </Button>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Sparkles className="h-4 w-4" />
                                <p className="text-sm">AI-generated compliance information coming soon...</p>
                              </div>
                            </div>
                          );
                        })()}



                        {/* Action Button */}
                        <Button 
                          className="w-full"
                          onClick={() => navigate(`/compliance/assets?regime=${selectedRegime}`)}
                        >
                          View All Assets for this Regulation
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    </div>
                  )}

                  {/* Location View */}
                  {viewType === 'location' && selectedLocationData && selectedLocation && (
                    <div className="space-y-6">
                        {/* Location Header */}
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-2xl font-bold">{selectedLocationData.locationName}</h3>
                              <p className="text-sm text-muted-foreground mt-1">Location Details</p>
                            </div>
                            <Badge 
                              variant={
                                selectedLocationData.complianceScore >= 90 ? "default" : 
                                selectedLocationData.complianceScore >= 75 ? "secondary" : 
                                "destructive"
                              }
                              className="text-lg px-3 py-1"
                            >
                              {selectedLocationData.complianceScore.toFixed(0)}%
                            </Badge>
                          </div>
                        </div>

                        <Separator />

                        {/* Location Statistics */}
                        <div className="space-y-4">
                          <h4 className="font-semibold flex items-center gap-2">
                            <BarChart3 className="h-4 w-4" />
                            Location Statistics
                          </h4>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Total Assets</p>
                              <p className="text-2xl font-bold">{selectedLocationData.totalAssets}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Compliant Assets</p>
                              <p className="text-2xl font-bold text-green-600">{selectedLocationData.compliant}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Non-Compliant</p>
                              <p className="text-2xl font-bold text-red-600">{selectedLocationData.totalAssets - selectedLocationData.compliant}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Compliance Rate</p>
                              <p className="text-2xl font-bold">{selectedLocationData.complianceScore.toFixed(1)}%</p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">Overall Progress</p>
                            <Progress value={selectedLocationData.complianceScore} className="h-3" />
                          </div>
                        </div>



                        {/* Action Button */}
                        <Button 
                          className="w-full"
                          onClick={() => navigate(`/compliance/assets?location=${selectedLocationData.locationName}`)}
                        >
                          View All Assets at this Location
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    </div>
                  )}

                  {/* Category View */}
                  {viewType === 'category' && selectedCategoryData && selectedCategory && (
                    <div className="space-y-6">
                        {/* Category Header */}
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-2xl font-bold">{selectedCategoryData.category}</h3>
                              <p className="text-sm text-muted-foreground mt-1">Asset Category Details</p>
                            </div>
                            <Badge 
                              variant={
                                selectedCategoryData.complianceScore >= 90 ? "default" : 
                                selectedCategoryData.complianceScore >= 75 ? "secondary" : 
                                "destructive"
                              }
                              className="text-lg px-3 py-1"
                            >
                              {selectedCategoryData.complianceScore.toFixed(0)}%
                            </Badge>
                          </div>
                        </div>

                        <Separator />

                        {/* Category Statistics */}
                        <div className="space-y-4">
                          <h4 className="font-semibold flex items-center gap-2">
                            <BarChart3 className="h-4 w-4" />
                            Category Statistics
                          </h4>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Total Assets</p>
                              <p className="text-2xl font-bold">{selectedCategoryData.totalAssets}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Compliant Assets</p>
                              <p className="text-2xl font-bold text-green-600">{selectedCategoryData.compliant}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Non-Compliant</p>
                              <p className="text-2xl font-bold text-red-600">{selectedCategoryData.totalAssets - selectedCategoryData.compliant}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Compliance Rate</p>
                              <p className="text-2xl font-bold">{selectedCategoryData.complianceScore.toFixed(1)}%</p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">Overall Progress</p>
                            <Progress value={selectedCategoryData.complianceScore} className="h-3" />
                          </div>
                        </div>



                        {/* Action Button */}
                        <Button 
                          className="w-full"
                          onClick={() => navigate(`/compliance/assets?category=${selectedCategoryData.category}`)}
                        >
                          View All Assets in this Category
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assets Requiring Attention */}
          <Card className="overflow-hidden">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Assets Requiring Attention
                  </CardTitle>
                  <CardDescription>
                    Assets not compliant or due soon, ordered by next compliance check
                  </CardDescription>
                </div>
                <Badge variant="outline" className="h-7 px-3 text-sm font-bold">
                  {assetList.filter(a => a.status === 'overdue' || a.status === 'due-soon').length} Assets
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                    <TableRow className="border-t-2 border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                      <TableHead className="h-12 font-semibold w-[250px]">Asset</TableHead>
                      <TableHead className="h-12 font-semibold w-[150px]">Location</TableHead>
                      <TableHead className="h-12 font-semibold w-[120px]">Regulation</TableHead>
                      <TableHead className="h-12 font-semibold w-[130px]">Next Due</TableHead>
                      <TableHead className="h-12 font-semibold w-[100px]">Status</TableHead>
                      <TableHead className="h-12 font-semibold w-[150px]">Assigned To</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assetList
                      .filter(a => a.status === 'overdue' || a.status === 'due-soon')
                      .sort((a, b) => {
                        const dateA = a.nextDueDate ? new Date(a.nextDueDate).getTime() : 0;
                        const dateB = b.nextDueDate ? new Date(b.nextDueDate).getTime() : 0;
                        return dateA - dateB;
                      })
                      .slice(0, 100)
                      .map((asset, index) => {
                        const statusConfig = getStatusConfig(asset.status);
                        return (
                          <TableRow 
                            key={asset.assetId}
                            className={`
                              ${index % 2 === 0 
                                ? 'bg-white dark:bg-slate-950' 
                                : 'bg-slate-50/50 dark:bg-slate-900/50'
                              }
                              hover:bg-slate-100 dark:hover:bg-slate-800 
                              transition-colors 
                              border-b border-slate-100 dark:border-slate-800
                            `}
                          >
                            <TableCell className="px-4 py-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssetClick(asset.assetId);
                                }}
                                className="text-left hover:underline focus:outline-none focus:underline"
                              >
                                <div className="font-semibold text-sm text-primary hover:text-primary/80">{asset.assetName}</div>
                                <div className="text-xs text-muted-foreground font-mono">{asset.assetId}</div>
                              </button>
                            </TableCell>
                            <TableCell className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              <span className="text-sm">{asset.location}</span>
                            </TableCell>
                            <TableCell className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              <span className="text-sm font-medium">{asset.regimeCode}</span>
                            </TableCell>
                            <TableCell className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              <span className="text-sm">
                                {asset.nextDueDate ? format(new Date(asset.nextDueDate), 'dd MMM yyyy') : 'N/A'}
                              </span>
                            </TableCell>
                            <TableCell className="px-4 py-3">
                              <Badge 
                                variant={asset.status === 'overdue' ? 'destructive' : 'outline'}
                                className={cn(
                                  "font-semibold",
                                  asset.status === 'due-soon' && "border-amber-400 bg-amber-100 text-amber-800 dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-300"
                                )}
                              >
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="px-4 py-3 text-slate-700 dark:text-slate-300">
                              {asset.assignedTo && (
                                <div className="flex items-center gap-1.5">
                                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                  <span className="text-sm">{asset.assignedTo}</span>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
              {assetList.filter(a => a.status === 'overdue' || a.status === 'due-soon').length === 0 && (
                <div className="text-center py-12 px-4">
                  <div className="inline-flex p-5 bg-green-50 dark:bg-green-950/20 rounded-2xl mb-4">
                    <CheckCircle className="h-14 w-14 text-green-500" />
                  </div>
                  <h4 className="font-bold text-xl mb-2">All Clear!</h4>
                  <p className="text-muted-foreground text-sm max-w-xs mx-auto">
                    No assets require immediate attention. All compliance checks are up to date.
                  </p>
                </div>
              )}
              <div className="border-t bg-muted/30 p-4">
                <Button
                  variant="outline"
                  size="default"
                  className="w-full group hover:bg-primary/5 hover:border-primary hover:text-primary font-semibold"
                  onClick={() => navigate('/compliance/assets')}
                >
                  <span>View All {summary.totalAssets} Assets</span>
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </CardContent>
          </Card>
            </>
          )}

        </div>
        </div>

        {/* Right Panel - Smart Insights (Full Height) - Hidden on smaller screens */}
        <div className={`hidden xl:block flex-shrink-0 h-full transition-all duration-300 ${isInsightsPanelCollapsed ? 'w-16' : 'w-[350px]'}`}>
          <InsightsPanel 
            insights={insights}
            isCollapsed={isInsightsPanelCollapsed}
            onToggleCollapse={() => setIsInsightsPanelCollapsed(!isInsightsPanelCollapsed)}
          />
        </div>
      </div>

      {/* Navigation Confirmation Dialog */}
      <AlertDialog open={navigationDialog.open} onOpenChange={(open) => setNavigationDialog({ open, assetId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Navigate to Asset Details?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be taken to the asset details page. Any unsaved changes on this page will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmNavigation}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
